import React, { useMemo } from 'react';
import { SyutSelect } from 'components/ui/SyutSelect';
import type { TaxPack } from 'api/types/tax.types';
import { addressStates, findState, usesStatePicker } from './taxAddress';

/**
 * Address state (PMS slice 3a-ii). For a home-country address the state is picked
 * from the tax pack's list, so the name always maps to a statutory state code (an
 * unregistered party is taxed by it). A foreign address keeps free text. A stored
 * name not in the list (entered before the picker) is still shown, flagged, so it
 * is never silently lost.
 */
interface Props {
  value: string;
  onChange: (name: string) => void;
  pack: TaxPack | null;
  country?: string;
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  field?: string;
}

const StateSelect: React.FC<Props> = ({
  value, onChange, pack, country, label = 'State', required, error, disabled, field = 'state',
}) => {
  const picker = usesStatePicker(pack, country);
  const known = findState(pack, value);

  const options = useMemo(() => {
    const list = addressStates(pack).map((p) => ({ value: p.placeName, label: `${p.placeName} (${p.placeCode})` }));
    if (value && !findState(pack, value)) list.unshift({ value, label: `${value} — not in the state list` });
    return list;
  }, [pack, value]);

  const labelNode = <>{label} {required && <span className="text-danger">*</span>}</>;

  if (!picker) {
    return (
      <div className="form-group" data-field={field}>
        <label className="form-label">{labelNode}</label>
        <input
          type="text"
          className={`form-input${error ? ' form-field-error' : ''}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="State / province"
          maxLength={100}
          disabled={disabled}
        />
        {error && <div className="form-error-message">{error}</div>}
      </div>
    );
  }

  return (
    <div className="form-group" data-field={field}>
      <SyutSelect
        label={label}
        required={required}
        options={options}
        value={value}
        onChange={(v) => onChange((v as string) || '')}
        placeholder="Select state..."
        isClearable={!required}
        isSearchable
        isDisabled={disabled}
        size="md"
        menuPortalTarget={document.body}
        error={error}
        helperText={!error && value && !known ? 'Pick the state from the list — the tax state is taken from it.' : undefined}
      />
    </div>
  );
};

export default StateSelect;
